import type { ThemePreset } from "../types";
import {
  spacing,
  radius as forestRadius,
  shadows as tokenShadows,
  fontSize,
  lineHeight,
  fontWeight,
  display,
  container,
  widths,
  alpha,
  base,
  error,
  warning,
  success,
  misc,
  gray,
  red,
  violet,
  magenta,
} from "../tokens";

// ─── Info Scale Override ─────────────────────────────────────────
// In Alkemy+, "info" is violet — links, selected states, info alerts,
// and Chip `colorInfo` all pick up violet automatically through this
// preset-local override. Other presets continue using the shared
// blue info scale.

const info = violet;

// ─── Highlight Scale ─────────────────────────────────────────────
// Magenta accent used sparingly for "NEW", "Premium", featured alerts,
// notification dots — high-signal, distinctive emphasis.

const highlight = magenta;

// ─── Semantic Colors — resolve against imported primitives ────────────────────────────


// Colors object using imported primitives
// Agency brand is near-black, so we use gray[950] as primary brand color
const colors = {
  base,
  error,
  warning,
  success,
  info,
  brand: {
    25: gray[25],
    50: gray[50],
    100: gray[100],
    200: gray[200],
    300: gray[300],
    400: gray[400],
    500: gray[500],
    600: gray[900],  // #18181b - dark brand
    700: gray[950],  // #09090b - primary brand
    800: gray[950],
    900: gray[950],
    950: gray[950],
  },
  accent: red,
  gray: gray,
};

// ─── Border Radius — minimal / sharp ────────────────────────────

const radius = {
  ...forestRadius,
  xxs: 1,
  xs: 2,
  sm: 3,
  md: 4,
  lg: 5,
  xl: 6,
  "2xl": 8,
  "3xl": 10,
  "4xl": 12,
};

// ─── Semantic Colors — Text ──────────────────────────────────────
// Same formulas as forest, resolved against alkemy+ primitives.

const text = {
  primary: gray[900],
  secondary: gray[700],
  secondaryHover: gray[800],
  tertiary: gray[600],
  tertiaryHover: gray[700],
  quaternary: gray[500],
  disabled: gray[500],
  placeholder: gray[500],
  placeholderSubtle: gray[300],
  white: base.white,
  brandPrimary: colors.brand[900],
  brandSecondary: colors.brand[700],
  brandTertiary: colors.brand[600],
  brandTertiaryAlt: colors.brand[600],
  primaryOnBrand: base.white,
  secondaryOnBrand: colors.brand[200],
  tertiaryOnBrand: colors.brand[200],
  quaternaryOnBrand: colors.brand[300],
  errorPrimary: error[600],
  warningPrimary: warning[600],
  successPrimary: success[600],
} as const;

// ─── Semantic Colors — Background ────────────────────────────────

const bg = {
  primary: gray[25],
  primaryAlt: gray[25],
  primaryHover: gray[100],
  primarySolid: gray[950],
  secondary: base.white,
  secondaryAlt: base.white,
  secondarySubtle: gray[25],
  secondaryHover: gray[50],
  secondarySolid: gray[600],
  tertiary: gray[50],
  quaternary: gray[100],
  active: gray[200],
  disabled: gray[100],
  disabledSubtle: gray[50],
  overlay: gray[950],
  brandPrimary: colors.brand[50],
  brandPrimaryAlt: colors.brand[50],
  brandSecondary: colors.brand[100],
  brandSolid: colors.brand[600],
  brandSolidHover: colors.brand[700],
  brandSection: colors.brand[800],
  brandSectionSubtle: colors.brand[700],
  errorPrimary: error[50],
  errorSecondary: error[100],
  errorSolid: error[600],
  warningPrimary: warning[50],
  warningSecondary: warning[100],
  warningSolid: warning[600],
  successPrimary: success[50],
  successSecondary: success[100],
  successSolid: success[600],
} as const;

// ─── Semantic Colors — Foreground ────────────────────────────────

const fg = {
  primary: gray[900],
  secondary: gray[700],
  secondaryHover: gray[800],
  tertiary: gray[600],
  tertiaryHover: gray[700],
  quaternary: gray[500],
  quaternaryHover: gray[600],
  quinary: gray[400],
  quinaryHover: gray[500],
  senary: gray[300],
  white: base.white,
  disabled: gray[400],
  disabledSubtle: gray[300],
  brandPrimary: colors.brand[600],
  brandPrimaryAlt: colors.brand[600],
  brandSecondary: colors.brand[500],
  errorPrimary: error[600],
  errorSecondary: error[500],
  warningPrimary: warning[600],
  warningSecondary: warning[500],
  successPrimary: success[600],
  successSecondary: success[500],
} as const;

// ─── Semantic Colors — Border ────────────────────────────────────

const border = {
  primary: gray[300],
  secondary: gray[200],
  tertiary: gray[100],
  disabled: gray[300],
  disabledSubtle: gray[200],
  brand: gray[400],
  brandSolid: colors.brand[600],
  brandSolidAlt: colors.brand[600],
  error: error[300],
  errorSolid: error[600],
} as const;

// ─── Component Colors — Buttons ──────────────────────────────────

const buttonColors = {
  primary: {
    bg: colors.brand[700],
    bgHover: colors.brand[800],
    fg: base.white,
    fgHover: base.white,
    border: colors.brand[700],
    borderHover: colors.brand[800],
  },
  secondary: {
    bg: gray[900],
    bgHover: gray[950],
    fg: base.white,
    fgHover: base.white,
    border: gray[900],
    borderHover: gray[950],
  },
  secondaryColor: {
    bg: base.white,
    bgHover: colors.brand[50],
    fg: colors.brand[700],
    fgHover: colors.brand[800],
    border: colors.brand[300],
    borderHover: colors.brand[300],
  },
  tertiary: {
    fg: gray[600],
    fgHover: gray[700],
    bgHover: gray[50],
  },
  tertiaryColor: {
    fg: colors.brand[700],
    fgHover: colors.brand[800],
    bgHover: colors.brand[50],
  },
  primaryError: {
    bg: error[600],
    bgHover: error[700],
    fg: base.white,
    fgHover: base.white,
    border: error[600],
    borderHover: error[700],
  },
  secondaryError: {
    bg: base.white,
    bgHover: error[50],
    fg: error[700],
    fgHover: error[800],
    border: error[300],
    borderHover: error[300],
  },
  tertiaryError: {
    fg: error[700],
    fgHover: error[800],
    bgHover: error[50],
  },
} as const;

// ─── Component Colors — Avatars ──────────────────────────────────

const avatarColors = {
  bg: gray[100],
  contrastBorder: "#00000014",
  profilePhotoBorder: base.white,
  focusBorder: "#a0a0a624", // gray[400] + 14% alpha
} as const;

// ─── Component Colors — Breadcrumbs ──────────────────────────────

const breadcrumbColors = {
  fg: gray[600],
  fgHover: gray[700],
  bgHover: gray[50],
  brandBgHover: colors.brand[50],
  brandFgHover: colors.brand[700],
  iconFg: gray[500],
  iconFgHover: gray[700],
  brandIconFgHover: colors.brand[700],
} as const;

// ─── Component Colors — Icons ────────────────────────────────────

const iconColors = {
  fgBrand: colors.brand[600],
  fgBrandOnBrand: colors.brand[200],
  featuredModernBorder: gray[200],
  featuredLightFgBrand: colors.brand[600],
  featuredLightFgGray: gray[500],
  featuredLightFgError: error[600],
  featuredLightFgWarning: warning[600],
  featuredLightFgSuccess: success[600],
  featuredDarkFgBrand: base.white,
  featuredDarkFgGray: base.white,
  featuredDarkFgError: base.white,
  featuredDarkFgWarning: base.white,
  featuredDarkFgSuccess: base.white,
} as const;

// ─── Component Colors — Navigation ──────────────────────────────

const navColors = {
  itemIconFg: gray[500],
  itemIconFgActive: gray[700],
  itemButtonIconFg: gray[500],
  itemButtonIconFgActive: gray[700],
} as const;

// ─── Component Colors — Sliders ──────────────────────────────────

const sliderColors = {
  handleBorder: colors.brand[600],
  handleBg: base.white,
} as const;

// ─── Component Colors — Toggles ──────────────────────────────────

const toggleColors = {
  buttonFgDisabled: gray[50],
} as const;

// ─── Component Colors — Charts ───────────────────────────────────

const chartColors = {
  axisFg: gray[100],
  gridlineFg: gray[100],
  labelFg: gray[600],
  labelFgEmphasis: gray[700],
  bg: base.white,
  bgAlt: gray[50],
  tooltipBg: gray[950],
  tooltipFg: base.white,
  crosshairFg: gray[500],
  legendFg: text.tertiary,
  series: [
    // Brand accents lead — violet + magenta anchor the palette
    { fg: violet[500], fgHover: violet[600], bg: violet[100], bgHover: violet[200] },
    { fg: magenta[500], fgHover: magenta[600], bg: magenta[100], bgHover: magenta[200] },
    // Complementary rotation — cool → warm → earth
    { fg: misc.teal[300], fgHover: misc.teal[400], bg: misc.teal[50], bgHover: misc.teal[100] },
    { fg: warning[400], fgHover: warning[500], bg: warning[100], bgHover: warning[200] },
    { fg: misc.blueLight[400], fgHover: misc.blueLight[500], bg: misc.blueLight[100], bgHover: misc.blueLight[200] },
    { fg: misc.rose[400], fgHover: misc.rose[500], bg: misc.rose[100], bgHover: misc.rose[200] },
    { fg: misc.green[500], fgHover: misc.green[600], bg: misc.green[100], bgHover: misc.green[200] },
    { fg: misc.orangeDark[400], fgHover: misc.orangeDark[500], bg: misc.orangeDark[100], bgHover: misc.orangeDark[200] },
    { fg: misc.cyan[600], fgHover: misc.cyan[700], bg: misc.cyan[100], bgHover: misc.cyan[200] },
    { fg: misc.orange[300], fgHover: misc.orange[400], bg: misc.orange[100], bgHover: misc.orange[200] },
    { fg: misc.teal[600], fgHover: misc.teal[700], bg: misc.teal[100], bgHover: misc.teal[200] },
    // Pink/fuchsia kept as tail fallbacks only — too close to magenta to sit near position 1
    { fg: misc.pink[400], fgHover: misc.pink[500], bg: misc.pink[100], bgHover: misc.pink[200] },
  ],
} as const;

// ─── Letter Spacing ──────────────────────────────────────────────
// Alkemy+ uses a consistent wide tracking across all text styles.

const letterSpacing = {
  h1: "-0.02em",
  h2: "-0.02em",
  h3: "-0.02em",
  h4: "-0.02em",
  h5: "-0.02em",
  h6: "-0.02em",
  subtitle1: "0.02em",
  subtitle2: "0.02em",
  body1: "0.02em",
  body2: "0.02em",
  caption: "0.02em",
  overline: "0.02em",
  button: "0.02em",
} as const;

// ─── Focus Rings ─────────────────────────────────────────────────
// brand[500] = #404040, gray[400] = #a0a0a6

const focusRings = {
  brand: "0px 0px 0px 4px #4040403d",
  brandShadowXs: "0px 1px 2px 0px #1018280d, 0px 0px 0px 4px #4040403d",
  brandShadowSm: "0px 1px 3px 0px #1018281a, 0px 1px 2px 0px #1018280f, 0px 0px 0px 4px #4040403d",
  gray: "0px 0px 0px 4px #a0a0a624",
  grayShadowXs: "0px 1px 2px 0px #1018280d, 0px 0px 0px 4px #a0a0a624",
  grayShadowSm: "0px 1px 3px 0px #1018281a, 0px 1px 2px 0px #1018280f, 0px 0px 0px 4px #a0a0a624",
  error: "0px 0px 0px 4px #f044383d",
  errorShadowXs: "0px 1px 2px 0px #1018280d, 0px 0px 0px 4px #f044383d",
} as const;

// ─── Preset ──────────────────────────────────────────────────────

export const forestAlkemyPlusPreset: ThemePreset = {
  name: "forest-alkemy+",
  defaultVariant: "light",
  variants: {
    light: {
      tokens: {
        base,
        error,
        warning,
        success,
        info,
        spacing,
        radius,
        shadows: tokenShadows,
        focusRings,
        fontFamily: '"Aeonik", sans-serif',
        fontFamilyMono: '"Aeonik Mono", monospace',
        fontSize,
        lineHeight,
        fontWeight,
        display,
        container,
        widths,
        text,
        bg,
        fg,
        border,
        buttonColors,
        avatarColors,
        breadcrumbColors,
        iconColors,
        navColors,
        sliderColors,
        toggleColors,
        chartColors,
        alpha,
        letterSpacing,
        highlight,
      },
    },
  },
};
